import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  MdClose, MdSearch, MdAdd, MdRocketLaunch,
  MdChevronLeft, MdChevronRight, MdMale, MdFemale,
  MdCheckCircle,
} from 'react-icons/md';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  generateCollectionsRequest,
  manualCollectionRequest,
} from '../../redux/slices/collectionsSlice';
import RichTextEditor from '../common/RichTextEditor';
import collectionsApi, {
  type CollectionProduct,
  type CollectionFiltersResponse,
} from '../../api/collections';
import type { ManualCollectionProductPayload } from '../../types/collections.types';

type CreationMode = 'ai' | 'manual';

interface CollectionCreationPanelProps {
  onClose: () => void;
  initialMode?: CreationMode;
}

const ALL_ITEMS = 'ALL ITEMS';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CollectionCreationPanel: React.FC<CollectionCreationPanelProps> = ({
  onClose,
  initialMode = 'ai',
}) => {
  const dispatch = useAppDispatch();
  const generating = useAppSelector((s) => s.collections.generating);

  // Mode
  const [mode, setMode] = useState<CreationMode>(initialMode);

  // Shared fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // AI-specific
  const [collectionCount, setCollectionCount] = useState('3');
  const [productCount, setProductCount] = useState('10');
  const [brandProfile, setBrandProfile] = useState(true);
  const [designEnsemble, setDesignEnsemble] = useState(true);

  // Manual-specific: filters & product browser
  const [filters, setFilters] = useState<CollectionFiltersResponse>({ categories: [], filters: {} });
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState(ALL_ITEMS);
  const [selectedGenders, setSelectedGenders] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<CollectionProduct[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [searching, setSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<CollectionProduct[]>([]);
  const filtersLoadedRef = useRef(false);
  const initialProductLoadRef = useRef(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Category scroll
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Selected SKU set for O(1) lookup
  const selectedSkus = useMemo(
    () => new Set(selectedProducts.map((p) => p.sku)),
    [selectedProducts]
  );

  // Track which filter category each selected product belongs to (sku -> category tab name)
  const [productCategoryMap, setProductCategoryMap] = useState<Record<string, string>>({});

  // ---------------------------------------------------------------------------
  // Load filters when switching to manual mode
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (mode === 'manual' && !filtersLoadedRef.current) {
      filtersLoadedRef.current = true;
      loadFilters();
    }
  }, [mode]);

  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    try {
      const data = await collectionsApi.fetchCollectionFilters();
      setFilters(data);
    } catch {
      setFilters({ categories: [], filters: {} });
    } finally {
      setFiltersLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Build gender payload (Unisex auto-included)
  // ---------------------------------------------------------------------------
  const buildGenderPayload = useCallback((genders: Set<string>): string[] => {
    if (genders.size === 0) return [];
    const result = new Set<string>(genders);
    result.add('Unisex');
    return Array.from(result);
  }, []);

  // ---------------------------------------------------------------------------
  // Search products
  // ---------------------------------------------------------------------------
  const searchProducts = useCallback(async (
    term: string,
    categories: string[],
    genders: Set<string>,
  ) => {
    setSearching(true);
    try {
      const data = await collectionsApi.searchCollectionProducts({
        search_term: term,
        categories,
        gender: buildGenderPayload(genders),
      });
      setProducts(data.products || []);
      setTotalResults(data.total_results || 0);
    } catch {
      setProducts([]);
      setTotalResults(0);
    } finally {
      setSearching(false);
    }
  }, [buildGenderPayload]);

  // ---------------------------------------------------------------------------
  // Debounced search for text input (500ms delay)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (mode !== 'manual' || filtersLoading) return;
    if (!initialProductLoadRef.current) return; // wait for initial load

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      const cats = activeCategory === ALL_ITEMS ? [] : [activeCategory];
      searchProducts(searchQuery, cats, selectedGenders);
    }, 500);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
    // Only searchQuery triggers debounced search
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // ---------------------------------------------------------------------------
  // Immediate search for filter changes (category / gender) + initial load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (mode !== 'manual' || filtersLoading) return;

    // Cancel any pending debounced search
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    // Initial load or filter change — fire immediately
    if (!initialProductLoadRef.current) {
      initialProductLoadRef.current = true;
    }
    const cats = activeCategory === ALL_ITEMS ? [] : [activeCategory];
    searchProducts(searchQuery, cats, selectedGenders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, selectedGenders, mode, filtersLoading]);

  // ---------------------------------------------------------------------------
  // Resolve product category (prefer category_name, fallback to category)
  // ---------------------------------------------------------------------------
  const getProductCategory = useCallback((p: CollectionProduct): string => {
    return p.category_name || p.category || 'Other';
  }, []);

  // ---------------------------------------------------------------------------
  // Group products by category
  // ---------------------------------------------------------------------------
  const groupedProducts = useMemo(() => {
    const groups: Record<string, CollectionProduct[]> = {};
    for (const p of products) {
      const cat = getProductCategory(p);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [products, getProductCategory]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const toggleGender = useCallback((gender: string) => {
    setSelectedGenders(prev => {
      const next = new Set(prev);
      if (next.has(gender)) next.delete(gender);
      else next.add(gender);
      return next;
    });
  }, []);

  const toggleProduct = useCallback((product: CollectionProduct) => {
    setSelectedProducts((prev) => {
      const exists = prev.some((p) => p.sku === product.sku);
      if (exists) {
        setProductCategoryMap((m) => { const n = { ...m }; delete n[product.sku]; return n; });
        return prev.filter((p) => p.sku !== product.sku);
      }
      // Record active filter category tab for accurate badge counts
      const filterCat = activeCategory !== ALL_ITEMS
        ? activeCategory
        : (product.category_name || product.category || 'Other');
      setProductCategoryMap((m) => ({ ...m, [product.sku]: filterCat }));
      return [...prev, product];
    });
  }, [activeCategory]);

  const removeProduct = useCallback((sku: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.sku !== sku));
    setProductCategoryMap((m) => { const n = { ...m }; delete n[sku]; return n; });
  }, []);

  const clearAllProducts = useCallback(() => {
    setSelectedProducts([]);
    setProductCategoryMap({});
  }, []);

  // Category scroll
  const handleCategoryScroll = useCallback(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  const scrollCategories = useCallback((dir: 'left' | 'right') => {
    categoryScrollRef.current?.scrollBy({
      left: dir === 'left' ? -180 : 180,
      behavior: 'smooth',
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const canSubmit = useMemo(() => {
    if (!title.trim()) return false;
    if (generating) return false;
    if (mode === 'manual' && selectedProducts.length === 0) return false;
    return true;
  }, [title, generating, mode, selectedProducts]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;

    const cleanDesc = description.replace(/<[^>]*>/g, '').trim();

    if (mode === 'ai') {
      dispatch(generateCollectionsRequest({
        search_term: `${title} ${cleanDesc}`.trim(),
        category: [],
        collection_count: parseInt(collectionCount) || 3,
        product_count: parseInt(productCount) || 10,
        brand_context_enabled: brandProfile,
        add_as_a_collection: designEnsemble,
        theme_title: title,
        theme_description: cleanDesc,
      }));
    } else {
      const productsList: ManualCollectionProductPayload[] = selectedProducts.map((p) => ({
        sku: p.sku,
        lowercase_title: p.lowercase_title,
        category: p.category,
        category_llm: p.category_llm,
        modified_price: p.modified_price,
        gender: p.gender,
        image: p.image,
      }));

      dispatch(manualCollectionRequest({
        theme_title: title,
        theme_description: cleanDesc,
        collection_products: productsList,
      }));
    }
  }, [canSubmit, mode, title, description, collectionCount, productCount, brandProfile, designEnsemble, selectedProducts, dispatch]);

  // Derived
  const allCategories = [ALL_ITEMS, ...filters.categories];
  const genders = filters.filters?.gender || [];
  const displayableGenders = genders.filter(g => g !== 'Unisex');

  // Count selected products per category using tracked category map
  const selectedCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of selectedProducts) {
      const cat = productCategoryMap[p.sku] || getProductCategory(p);
      counts[cat] = (counts[cat] || 0) + 1;
    }
    counts[ALL_ITEMS] = selectedProducts.length;
    return counts;
  }, [selectedProducts, productCategoryMap, getProductCategory]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#141414]">
      {/* ── Header ── */}
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-3 flex-shrink-0">
        {/* Title */}
        <h2 className="text-[15px] font-semibold text-white flex-1">Create Collection</h2>

        {/* Generate / Create button */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold text-white transition-all cursor-pointer hover:brightness-110 active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: canSubmit ? 'linear-gradient(135deg, #EE4423, #D63384)' : '#333' }}
        >
          <MdRocketLaunch size={14} />
          {generating ? 'Generating...' : 'Generate'}
        </button>

        {/* Close icon */}
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors cursor-pointer"
          title="Close"
        >
          <MdClose size={18} />
        </button>
      </div>

      {/* ── Form ── */}
      <div
        className={`${mode === 'manual' ? 'flex-1 flex flex-col overflow-hidden min-h-0' : 'flex-1 overflow-y-auto'} px-5 py-4 space-y-5`}
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.06) transparent' }}
      >
        {/* Title */}
        <div>
          <label className="block text-[12px] font-medium text-gray-400 mb-1.5">
            {mode === 'ai' ? 'Theme Title' : 'Collection Title'}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={mode === 'ai' ? 'e.g. Modern Living Room Ensemble' : 'e.g. Scandinavian Comfort Series'}
            className="w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[13px] text-white placeholder-gray-500 outline-none focus:border-orange-500/40 transition-colors"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-[12px] font-medium text-gray-400 mb-1.5">
            {mode === 'ai' ? 'Theme Description' : 'Collection Description'}
          </label>
          <RichTextEditor
            value={description}
            onChange={(val) => val !== '<p><br></p>' && val !== '<p></p>' && setDescription(val)}
            placeholder={mode === 'ai' ? 'Describe the furniture theme, style, and ambiance...' : 'Describe this furniture collection...'}
            isDarkMode={true}
            minHeight="100px"
          />
        </div>

        {/* ── AI-specific fields ── */}
        {mode === 'ai' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-medium text-gray-400 mb-1.5">Collections</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={collectionCount}
                  onChange={(e) => setCollectionCount(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[13px] text-white outline-none focus:border-orange-500/40 transition-colors"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-gray-400 mb-1.5">Products per collection</label>
                <input
                  type="number"
                  min="1"
                  max="15"
                  value={productCount}
                  onChange={(e) => setProductCount(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[13px] text-white outline-none focus:border-orange-500/40 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-3">
              <ToggleRow label="Brand Profile" desc="Align with brand identity" checked={brandProfile} onChange={setBrandProfile} />
              <ToggleRow label="Design Ensemble" desc="Create coordinated outfits" checked={designEnsemble} onChange={setDesignEnsemble} />
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ── Manual-specific: Product Browser with Filters ──              */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {mode === 'manual' && (
          <div className="flex-1 flex flex-col overflow-hidden min-h-0 space-y-4">
            {/* ── Selected Products Summary (shown at top when products are selected) ── */}
            {selectedProducts.length > 0 && (
              <div className="p-3 rounded-xl border border-[#EE4423]/20 bg-[#EE4423]/[0.03]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MdCheckCircle size={14} className="text-[#EE4423]" />
                    <span className="text-[12px] font-semibold text-orange-400">
                      {selectedProducts.length} Product{selectedProducts.length !== 1 ? 's' : ''} Selected
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedProducts([])}
                    className="text-[10px] text-gray-500 hover:text-red-400 transition-colors cursor-pointer"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedProducts.map((p) => (
                    <div
                      key={p.sku}
                      className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-lg bg-white/[0.04] border border-white/[0.06] group"
                    >
                      <img
                        src={p.image}
                        alt={p.lowercase_title}
                        className="w-6 h-6 rounded object-contain bg-white"
                      />
                      <span className="text-[10px] text-gray-300 capitalize max-w-[90px] truncate">
                        {p.lowercase_title}
                      </span>
                      <button
                        onClick={() => removeProduct(p.sku)}
                        className="text-gray-600 hover:text-red-400 transition-colors cursor-pointer ml-0.5"
                      >
                        <MdClose size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Search Bar ── */}
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] focus-within:border-orange-500/40 transition-colors">
              <MdSearch size={16} className="text-gray-500 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, colors, styles..."
                className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder-gray-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="p-0.5 rounded-full hover:bg-white/[0.08] text-gray-500 hover:text-white transition-colors cursor-pointer"
                >
                  <MdClose size={14} />
                </button>
              )}
            </div>

            {/* ── Category Tabs + Gender Toggles (same row) ── */}
            <div className="flex items-center gap-2">
              {/* Category Tabs (scrollable, takes remaining space) */}
              <div className="relative flex-1 min-w-0">
                {showLeftArrow && (
                  <button
                    onClick={() => scrollCategories('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-0.5 rounded-full bg-[#141414]/90 border border-white/[0.08] text-gray-400 hover:text-white transition-colors cursor-pointer shadow-lg"
                  >
                    <MdChevronLeft size={14} />
                  </button>
                )}

                <div
                  ref={categoryScrollRef}
                  onScroll={handleCategoryScroll}
                  className="flex gap-0.5 overflow-x-auto"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {filtersLoading ? (
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-6 w-14 rounded-md bg-white/[0.04] animate-pulse flex-shrink-0" />
                      ))}
                    </div>
                  ) : (
                    allCategories.map(cat => {
                      const isActive = activeCategory === cat;
                      const catCount = selectedCountByCategory[cat] || 0;
                      return (
                        <button
                          key={cat}
                          onClick={() => setActiveCategory(cat)}
                          className={`relative whitespace-nowrap px-2.5 py-1.5 text-[12px] font-semibold transition-all cursor-pointer flex-shrink-0 rounded-md flex items-center gap-1.5 ${
                            isActive
                              ? 'text-[#EE4423] bg-[#EE4423]/[0.08]'
                              : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]'
                          }`}
                        >
                          {cat}
                          {catCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold bg-[#EE4423] text-white leading-none">
                              {catCount}
                            </span>
                          )}
                          {isActive && (
                            <div className="absolute bottom-0 left-0.5 right-0.5 h-[2px] rounded-full bg-gradient-to-r from-[#EE4423] to-[#D63384]" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>

                {showRightArrow && (
                  <button
                    onClick={() => scrollCategories('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-0.5 rounded-full bg-[#141414]/90 border border-white/[0.08] text-gray-400 hover:text-white transition-colors cursor-pointer shadow-lg"
                  >
                    <MdChevronRight size={14} />
                  </button>
                )}
              </div>

              {/* Gender toggles (right end) */}
              {displayableGenders.length > 0 && (
                <div className="flex items-center gap-1 flex-shrink-0 pl-2 border-l border-white/[0.06]">
                  {displayableGenders.map(gender => {
                    const isActive = selectedGenders.has(gender);
                    return (
                      <button
                        key={gender}
                        onClick={() => toggleGender(gender)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[12px] font-medium transition-all cursor-pointer border ${
                          isActive
                            ? 'bg-gradient-to-r from-[#EE4423]/20 to-[#D63384]/20 border-[#EE4423]/40 text-white'
                            : 'bg-white/[0.03] border-white/[0.08] text-gray-400 hover:bg-white/[0.06] hover:text-white'
                        }`}
                      >
                        {gender === 'Male' ? <MdMale size={13} /> : <MdFemale size={13} />}
                        {gender}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Results count */}
            {totalResults > 0 && !searching && (
              <div className="text-[10px] text-gray-500 -mt-2">
                {totalResults.toLocaleString()} results
              </div>
            )}

            {/* ── Product Grid (dynamic height, separate scroll) ── */}
            <div
              className="flex-1 space-y-4"
              style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.06) transparent' }}
            >
              {searching ? (
                /* Loading skeleton */
                <div className="space-y-4">
                  {[...Array(2)].map((_, gi) => (
                    <div key={gi}>
                      <div className="h-4 w-20 rounded bg-white/[0.04] animate-pulse mb-2" />
                      <div className="grid grid-cols-6 gap-1.5">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="rounded-md bg-white/[0.03] border border-white/[0.06] overflow-hidden">
                            <div className="aspect-square bg-white/[0.04] animate-pulse" />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : groupedProducts.length > 0 ? (
                /* Product cards grouped by category */
                groupedProducts.map(([category, categoryProducts]) => (
                  <div key={category}>
                    {/* Category heading (only for ALL ITEMS) */}
                    {activeCategory === ALL_ITEMS && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <h4 className="text-[12px] font-bold text-white">{category}</h4>
                        <span className="text-[10px] text-gray-600">({categoryProducts.length})</span>
                      </div>
                    )}

                    <div className="grid grid-cols-6 gap-1.5">
                      {categoryProducts.map((product) => {
                        const isSelected = selectedSkus.has(product.sku);
                        return (
                          <button
                            key={product.sku}
                            onClick={() => toggleProduct(product)}
                            title={`${product.lowercase_title} — ${product.price || `$${product.modified_price}`}`}
                            className={`group relative rounded-md overflow-hidden text-left transition-all cursor-pointer border aspect-square ${
                              isSelected
                                ? 'border-[#EE4423]/50 ring-1 ring-[#EE4423]/30'
                                : 'border-white/[0.06] hover:border-white/[0.12]'
                            }`}
                          >
                            {/* Image fills the entire card */}
                            <img
                              src={product.image}
                              alt={product.lowercase_title}
                              className="absolute inset-0 w-full h-full object-cover object-center bg-white group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%231a1a2e"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="12" fill="%234a4a6a"%3ENo Image%3C/text%3E%3C/svg%3E';
                              }}
                            />

                            {/* Hover overlay with product name */}
                            <div className="absolute inset-0 z-[5] bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none flex items-end">
                              <p className="text-[8px] font-medium text-white capitalize line-clamp-2 leading-tight px-1.5 pb-1.5 w-full">
                                {product.lowercase_title} - {product.price}
                              </p>
                            </div>

                            {/* Selection check */}
                            <div className={`absolute top-1 right-1 z-10 transition-all duration-200 ${
                              isSelected ? 'opacity-100 scale-100' : 'opacity-0 scale-75 group-hover:opacity-50 group-hover:scale-100'
                            }`}>
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                isSelected
                                  ? 'bg-[#EE4423] shadow-lg shadow-[#EE4423]/30'
                                  : 'bg-black/40 backdrop-blur-sm border border-white/20'
                              }`}>
                                {isSelected ? (
                                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                ) : (
                                  <MdAdd size={10} className="text-white" />
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                /* Empty state */
                <div className="flex flex-col items-center justify-center py-10">
                  <div className="w-12 h-12 rounded-full bg-white/[0.04] flex items-center justify-center mb-3">
                    <MdSearch size={22} className="text-gray-600" />
                  </div>
                  <p className="text-[12px] text-gray-400 font-medium">No products found</p>
                  <p className="text-[10px] text-gray-600 mt-0.5">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>


      {/* Hide scrollbar for category tabs */}
      <style>{`
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Toggle Row (inline)
// ---------------------------------------------------------------------------
const ToggleRow: React.FC<{
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, desc, checked, onChange }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
    <div>
      <p className="text-[13px] font-medium text-gray-200">{label}</p>
      <p className="text-[11px] text-gray-500">{desc}</p>
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${checked ? 'bg-orange-500' : 'bg-gray-600'}`}
    >
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  </div>
);

CollectionCreationPanel.displayName = 'CollectionCreationPanel';
export default CollectionCreationPanel;
